import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRequireApproved } from '../lib/auth';
import { useCreateWorkspace } from '../lib/queries';
import { ORG_KINDS, type OrgKind } from '../data/orgKinds';

export function CreateWorkspacePage() {
  const { user, loading } = useRequireApproved();
  const navigate = useNavigate();
  const createMut = useCreateWorkspace();

  const [name, setName] = useState('');
  const [kind, setKind] = useState<OrgKind>('LEA');
  const [err, setErr] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center font-editorial italic text-ink-muted">
        Loading…
      </div>
    );
  }
  if (!user) return null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setErr('Give your workspace a name.');
      return;
    }

    try {
      const org = await createMut.mutateAsync({ name: trimmed, kind });
      navigate(`/workspace/${org.id}`, { replace: true });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-16 md:py-20">
      <div className="eyebrow mb-4">New workspace</div>
      <h1 className="editorial-h1 mb-3">
        Set up your <em className="italic text-accent">workspace.</em>
      </h1>
      <p className="editorial-deck mb-10">
        You'll be set as the owner. Invite teammates next.
      </p>

      <form onSubmit={onSubmit} className="space-y-7">
        <label className="block">
          <span className="font-label text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted block mb-2">
            Workspace name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. "Alameda COE — CYBHI FY26"'
            autoFocus
            required
            maxLength={200}
            className="w-full bg-transparent border-0 border-b border-rule focus:border-accent focus:outline-none font-editorial text-[18px] text-ink py-2 placeholder:text-ink-muted placeholder:italic"
          />
          <p className="font-editorial italic text-[13px] text-ink-muted mt-1">
            What your teammates will see in the masthead and on shared screens.
          </p>
        </label>

        <fieldset>
          <legend className="font-label text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted mb-3">
            Workspace type
          </legend>
          <div className="space-y-px bg-rule border border-rule">
            {ORG_KINDS.map((k) => {
              const selected = kind === k.id;
              return (
                <label
                  key={k.id}
                  className={
                    'flex items-center gap-4 p-3.5 bg-paper cursor-pointer transition-colors ' +
                    (selected ? 'bg-paper-deep' : 'hover:bg-paper-deep')
                  }
                >
                  <input
                    type="radio"
                    name="kind"
                    value={k.id}
                    checked={selected}
                    onChange={() => setKind(k.id)}
                    className="sr-only"
                  />
                  <span
                    className={
                      'inline-block w-4 h-4 rounded-full border-2 shrink-0 transition-colors ' +
                      (selected
                        ? 'border-accent bg-accent'
                        : 'border-ink-muted bg-transparent')
                    }
                    aria-hidden="true"
                  />
                  <span className="font-editorial text-[16px] text-ink">
                    <span className="font-label text-[10px] uppercase tracking-[0.18em] text-accent mr-3 align-middle">
                      {k.id}
                    </span>
                    {k.label.replace(/^[^\s]+\s/, '')}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {err && (
          <div className="font-editorial italic text-[14px] text-[#a02525]">
            ⚠ {err}
          </div>
        )}

        <div className="border-t border-rule pt-7 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/dashboard"
            className="font-label text-[11px] uppercase tracking-[0.15em] text-ink-muted hover:text-accent transition-colors"
          >
            ← cancel
          </Link>
          <button
            type="submit"
            disabled={createMut.isPending || !name.trim()}
            className={
              'btn-text ' +
              (createMut.isPending || !name.trim()
                ? 'opacity-40 cursor-not-allowed pointer-events-none'
                : '')
            }
          >
            {createMut.isPending ? 'Creating…' : 'Create workspace'}
          </button>
        </div>
      </form>
    </div>
  );
}
